/**
 * credits-store — Points-based freemium system.
 *
 * Plans:
 *   Free  — 30 pts/month, hard limits: 3 CV versions, 5 job offers saved
 *   Mini  — 300 pts/month, hard limits: 10 CV versions, 20 job offers saved
 *   Premium — unlimited points, unlimited CV versions & job offers
 *
 * Action costs (points deducted):
 *   aiAnalysis   → 3 pts
 *   compare      → 4 pts
 *   optimize     → 5 pts
 *   coverLetter  → 8 pts
 *   exportPdf    → 5 pts
 *   exportDocx   → 1 pt
 *   exportJson   → 0 pts  (free forever)
 *   print        → 0 pts  (free forever)
 *
 * Earn points (gamification):
 *   browseOffer  → +1 pt (credited when user opens a job offer page)
 *   saveOffer    → +2 pts (credited when user saves a job offer)
 *
 * Points reset monthly (calendar month boundary).
 */

// Import + re-export shared types/constants from action-costs.ts (partagé client+serveur)
import {
  ACTION_COSTS,
  PLAN_MONTHLY_BUDGET,
  PLAN_STORAGE_LIMITS,
  routeToActionType,
  type Plan,
  type ActionType,
} from './action-costs';
export type { Plan, ActionType };
export { ACTION_COSTS, PLAN_MONTHLY_BUDGET, PLAN_STORAGE_LIMITS, routeToActionType };

export type EarnType = 'browseOffer' | 'saveOffer';

export const EARN_REWARDS: Record<EarnType, number> = {
  browseOffer: 1,
  saveOffer: 2,
};

export interface PlanConfig {
  monthlyPoints: number | null; // null = unlimited
  cvVersionsMax: number | null;
  jobOffersMax: number | null;
  label: string;
  color: string;
}

export const PLAN_CONFIG: Record<Plan, PlanConfig> = {
  free: {
    monthlyPoints: 30,
    cvVersionsMax: PLAN_STORAGE_LIMITS.free.cvVersionsMax,
    jobOffersMax:  PLAN_STORAGE_LIMITS.free.jobOffersMax,
    label: 'Gratuit',
    color: 'text-white/50',
  },
  mini: {
    monthlyPoints: 300,
    cvVersionsMax: PLAN_STORAGE_LIMITS.mini.cvVersionsMax,
    jobOffersMax:  PLAN_STORAGE_LIMITS.mini.jobOffersMax,
    label: 'Mini',
    color: 'text-sky-400',
  },
  premium: {
    monthlyPoints: null,
    cvVersionsMax: PLAN_STORAGE_LIMITS.premium.cvVersionsMax,
    jobOffersMax:  PLAN_STORAGE_LIMITS.premium.jobOffersMax,
    label: 'Premium',
    color: 'text-amber-400',
  },
};

export interface PointsStore {
  /** YYYY-MM for the current billing month */
  month: string;
  /** Points spent this month */
  spent: number;
  /** Points earned via gamification this month */
  earned: number;
}

const POINTS_KEY = 'cv_points_v2';
const PLAN_KEY = 'cv_plan';

/** Backward-compat: migrate legacy cv_is_premium → premium plan */
function migrateLegacy(): void {
  try {
    if (!localStorage.getItem(PLAN_KEY) && localStorage.getItem('cv_is_premium') === 'true') {
      localStorage.setItem(PLAN_KEY, 'premium');
    }
  } catch { /* ignore */ }
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function emptyStore(): PointsStore {
  return { month: currentMonth(), spent: 0, earned: 0 };
}

export function getPlan(): Plan {
  migrateLegacy();
  try {
    const v = localStorage.getItem(PLAN_KEY);
    if (v === 'mini' || v === 'premium' || v === 'free') return v;
  } catch { /* ignore */ }
  return 'free';
}

export function setPlan(plan: Plan): void {
  try {
    localStorage.setItem(PLAN_KEY, plan);
    // keep legacy key in sync
    localStorage.setItem('cv_is_premium', plan === 'premium' ? 'true' : 'false');
  } catch { /* ignore */ }
}

export function getPointsStore(): PointsStore {
  try {
    const raw = localStorage.getItem(POINTS_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as PointsStore;
    if (parsed.month !== currentMonth()) return emptyStore();
    return parsed;
  } catch {
    return emptyStore();
  }
}

export function savePointsStore(store: PointsStore): void {
  try { localStorage.setItem(POINTS_KEY, JSON.stringify(store)); } catch { /* ignore */ }
}

// ── Spend ──────────────────────────────────────────────────────────────────

export function canPerformAction(action: ActionType): boolean {
  const plan = getPlan();
  if (plan === 'premium') return true;
  const cost = ACTION_COSTS[action];
  if (cost === 0) return true;
  const config = PLAN_CONFIG[plan];
  if (config.monthlyPoints === null) return true;
  const store = getPointsStore();
  const available = config.monthlyPoints + store.earned - store.spent;
  return available >= cost;
}

export function spendPoints(action: ActionType): boolean {
  const cost = ACTION_COSTS[action];
  if (cost === 0) return true;
  if (!canPerformAction(action)) return false;
  const store = getPointsStore();
  savePointsStore({ ...store, spent: store.spent + cost });
  return true;
}

// ── Earn ──────────────────────────────────────────────────────────────────

export function earnPoints(type: EarnType): void {
  const reward = EARN_REWARDS[type];
  if (reward === 0) return;
  const store = getPointsStore();
  // Cap earned bonus at 50% of monthly allowance to prevent farming
  const plan = getPlan();
  const config = PLAN_CONFIG[plan];
  const cap = config.monthlyPoints !== null ? Math.floor(config.monthlyPoints * 0.5) : 9999;
  const newEarned = Math.min(store.earned + reward, cap);
  savePointsStore({ ...store, earned: newEarned });
}

// ── Summary ───────────────────────────────────────────────────────────────

export interface PointsSummary {
  plan: Plan;
  planConfig: PlanConfig;
  /** Total points available this month (budget + earned) */
  totalAvailable: number | null; // null = unlimited
  /** Points spent this month */
  spent: number;
  /** Bonus points earned via gamification */
  earned: number;
  /** Remaining points (null = unlimited) */
  remaining: number | null;
  /** 0-100 usage percentage (0 for premium) */
  usagePercent: number;
}

export function getPointsSummary(): PointsSummary {
  const plan = getPlan();
  const config = PLAN_CONFIG[plan];
  const store = getPointsStore();

  if (config.monthlyPoints === null) {
    return {
      plan,
      planConfig: config,
      totalAvailable: null,
      spent: store.spent,
      earned: store.earned,
      remaining: null,
      usagePercent: 0,
    };
  }

  const totalAvailable = config.monthlyPoints + store.earned;
  const remaining = Math.max(0, totalAvailable - store.spent);
  const usagePercent = Math.min(100, Math.round((store.spent / totalAvailable) * 100));

  return {
    plan,
    planConfig: config,
    totalAvailable,
    spent: store.spent,
    earned: store.earned,
    remaining,
    usagePercent,
  };
}

// ── Hard limits ───────────────────────────────────────────────────────────

export function canAddCvVersion(currentCount: number): boolean {
  const config = PLAN_CONFIG[getPlan()];
  if (config.cvVersionsMax === null) return true;
  return currentCount < config.cvVersionsMax;
}

export function canSaveJobOffer(currentCount: number): boolean {
  const config = PLAN_CONFIG[getPlan()];
  if (config.jobOffersMax === null) return true;
  return currentCount < config.jobOffersMax;
}

// ── Legacy compat shims (used by useCredits.ts, FreemiumGate, Pricing, etc.) ─

/** @deprecated Use ActionType / PLAN_CONFIG instead */
export type CreditType = 'aiAnalysis' | 'cvGeneration' | 'coverLetter';

/** @deprecated Use PLAN_CONFIG[plan].monthlyPoints instead. Kept for backward compat only. */
export interface Credits {
  month: string;
  aiAnalysis: number;
  cvGeneration: number;
  coverLetter: number;
}

/** @deprecated Use PLAN_CONFIG[plan] instead. */
export interface FreemiumLimits {
  aiAnalysis: number;
  cvGeneration: number;
  coverLetter: number;
  cvVersions: number;
  jobOffers: number;
}

/** @deprecated Use PLAN_CONFIG instead. Free tier reference values only. */
export const FREE_LIMITS: FreemiumLimits = {
  aiAnalysis: 5,
  cvGeneration: 3,
  coverLetter: 2,
  cvVersions: 3,
  jobOffers: 5,
};

/** @deprecated Use getPointsStore/getPointsSummary instead. */
export function getCredits(): Credits {
  const store = getPointsStore();
  return {
    month: store.month,
    aiAnalysis: store.spent,
    cvGeneration: store.spent,
    coverLetter: store.spent,
  };
}

/** @deprecated Use spendPoints instead. */
export function consumeCredit(type: CreditType): Credits {
  const actionMap: Record<CreditType, ActionType> = {
    aiAnalysis: 'aiAnalysis',
    cvGeneration: 'optimize',
    coverLetter: 'coverLetter',
  };
  spendPoints(actionMap[type]);
  return getCredits();
}

/** @deprecated Use canPerformAction / spendPoints instead */
export function isPremium(): boolean {
  return getPlan() === 'premium';
}

/** @deprecated Use setPlan instead */
export function setPremium(value: boolean): void {
  setPlan(value ? 'premium' : 'free');
}

/** @deprecated Use canPerformAction instead */
export function hasCredits(type: 'aiAnalysis' | 'cvGeneration' | 'coverLetter'): boolean {
  const actionMap: Record<string, ActionType> = {
    aiAnalysis: 'aiAnalysis',
    cvGeneration: 'optimize',
    coverLetter: 'coverLetter',
  };
  return canPerformAction(actionMap[type] ?? 'aiAnalysis');
}

/** @deprecated Use getPointsSummary instead */
export function getRemainingCredits(_type: 'aiAnalysis' | 'cvGeneration' | 'coverLetter'): number {
  const summary = getPointsSummary();
  if (summary.remaining === null) return Infinity;
  return summary.remaining;
}

export function resetCredits(): void {
  savePointsStore(emptyStore());
}
